import { FetchError } from "@medusajs/js-sdk";
import { Button, Text, toast } from "@medusajs/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import {
  type SeedWorkflowState,
  useAdminSeedStatus,
  useAdminStartSeed,
} from "@/hooks/api/seed";

export function SettingsStoreSeedTrigger() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const isStoreSettings = pathname.startsWith("/settings/store");
  const [seedFlowPending, setSeedFlowPending] = useState(false);
  const timeoutToastShownRef = useRef(false);
  const startStateRef = useRef<SeedWorkflowState>("unknown");
  const observedRunningRef = useRef(false);

  const {
    workflowState,
    pollTimedOut,
    resetPollTimeout,
    isError: isStatusError,
    error: statusError,
  } = useAdminSeedStatus(isStoreSettings, seedFlowPending);

  const { mutateAsync, isPending: isStartPending } = useAdminStartSeed();

  useEffect(() => {
    if (!isStoreSettings) {
      setSeedFlowPending(false);
      timeoutToastShownRef.current = false;
      startStateRef.current = "unknown";
      observedRunningRef.current = false;
    }
  }, [isStoreSettings]);

  const startedFromCompleted = startStateRef.current === "completed";
  const waitingForFreshResult =
    seedFlowPending &&
    (workflowState === "running" ||
      workflowState === "unknown" ||
      workflowState === "idle" ||
      (workflowState === "completed" &&
        startedFromCompleted &&
        !observedRunningRef.current));
  const isServerRunning = workflowState === "running";
  const isSeedUiBusy =
    isStartPending ||
    (!pollTimedOut && (isServerRunning || waitingForFreshResult));

  useEffect(() => {
    if (!isStoreSettings) {
      return;
    }
    if (!pollTimedOut || !seedFlowPending) {
      return;
    }
    const stuck =
      workflowState === "running" ||
      workflowState === "unknown" ||
      workflowState === "idle" ||
      (workflowState === "completed" &&
        startedFromCompleted &&
        !observedRunningRef.current);
    if (!stuck) {
      return;
    }
    if (timeoutToastShownRef.current) {
      return;
    }
    timeoutToastShownRef.current = true;
    setSeedFlowPending(false);
    toast.error(t("store.seed.timeoutToast"));
  }, [
    isStoreSettings,
    pollTimedOut,
    seedFlowPending,
    startedFromCompleted,
    workflowState,
    t,
  ]);

  useEffect(() => {
    if (!isStoreSettings || !seedFlowPending) {
      return;
    }
    if (workflowState === "running") {
      observedRunningRef.current = true;
      return;
    }
    if (workflowState === "completed") {
      const canResolveAsSuccess =
        observedRunningRef.current || !startedFromCompleted;
      if (!canResolveAsSuccess) {
        return;
      }
      setSeedFlowPending(false);
      toast.success(t("store.seed.successToast"));
      return;
    }
    if (workflowState === "failed") {
      setSeedFlowPending(false);
      toast.error(t("store.seed.failedToast"));
    }
  }, [isStoreSettings, seedFlowPending, startedFromCompleted, workflowState, t]);

  useEffect(() => {
    if (!isStoreSettings || !isStatusError) {
      return;
    }
    if (!seedFlowPending) {
      return;
    }
    setSeedFlowPending(false);
    const message =
      statusError instanceof FetchError
        ? statusError.message
        : t("store.seed.statusErrorToast");
    toast.error(message || t("store.seed.statusErrorToast"));
  }, [isStoreSettings, isStatusError, statusError, seedFlowPending, t]);

  if (!isStoreSettings) {
    return null;
  }

  const handleClick = async () => {
    timeoutToastShownRef.current = false;
    resetPollTimeout();
    startStateRef.current = workflowState;
    observedRunningRef.current = false;
    setSeedFlowPending(true);
    try {
      await mutateAsync();
    } catch (error) {
      setSeedFlowPending(false);
      const message =
        error instanceof FetchError ? error.message : t("store.seed.errorToast");
      toast.error(message);
    }
  };

  return (
    <div className="px-3 pb-2">
      <div className="px-2 pb-2">
        <Text size="xsmall" leading="compact" className="text-ui-fg-subtle">
          {t("store.seed.description")}
        </Text>
      </div>
      {isSeedUiBusy ? (
        <div className="px-2 pb-2">
          <Text size="xsmall" leading="compact" className="text-ui-fg-muted">
            {t("store.seed.statusRunning")}
          </Text>
        </div>
      ) : null}
      <div className="px-2">
        <Button
          type="button"
          variant="secondary"
          size="small"
          className="w-full"
          disabled={isSeedUiBusy}
          isLoading={isSeedUiBusy}
          onClick={handleClick}
        >
          {t("store.seed.runButton")}
        </Button>
      </div>
    </div>
  );
}
